import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { appsApi, schemaApi, apiKeysApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const AppsContext = createContext(null);

export function AppsProvider({ children }) {
  const { isAdmin } = useAuth();
  const [apps, setApps] = useState([]);
  const [currentApp, setCurrentApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appReadiness, setAppReadiness] = useState({});

  // Refs to avoid stale closures in fetchApps
  const currentAppRef = useRef(currentApp);
  currentAppRef.current = currentApp;
  const checkReadinessRef = useRef(null);

  // Fetch all apps
  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await appsApi.list();
      const appsList = Array.isArray(response.data?.data) ? response.data.data : [];
      setApps(appsList);

      // Restore app from URL > localStorage > first app (only if not already set)
      if (!currentAppRef.current && appsList.length > 0) {
        // Check URL for appId (e.g., /apps/:appId/mind)
        const urlMatch = window.location.pathname.match(/\/apps\/([a-f0-9-]{36})\//);
        const urlAppId = urlMatch?.[1];
        const urlApp = urlAppId && appsList.find((a) => a.appId === urlAppId);

        const savedId = localStorage.getItem('currentAppId');
        const savedApp = savedId && appsList.find((a) => a.appId === savedId);

        const resolved = urlApp || savedApp || appsList[0];
        setCurrentApp(resolved);
        localStorage.setItem('currentAppId', resolved.appId);
        // Fire readiness check via ref (defined after this callback)
        setTimeout(() => checkReadinessRef.current?.(resolved.appId), 0);
      }

      return appsList;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch apps');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // No dependency on currentApp — uses ref

  // Fetch single app
  const fetchApp = useCallback(async (appId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await appsApi.get(appId);
      return response.data.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch app');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create app
  const createApp = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await appsApi.create(data);
      const newApp = response.data.data;
      setApps((prev) => [...prev, newApp]);
      return newApp;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create app');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update app
  const updateApp = useCallback(async (appId, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await appsApi.update(appId, data);
      const updatedApp = response.data.data;
      setApps((prev) =>
        prev.map((app) => (app.appId === appId ? updatedApp : app))
      );
      if (currentApp?.appId === appId) {
        setCurrentApp(updatedApp);
      }
      return updatedApp;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update app');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentApp]);

  // Delete app
  const deleteApp = useCallback(async (appId) => {
    try {
      setLoading(true);
      setError(null);
      await appsApi.delete(appId);
      setApps((prev) => prev.filter((app) => app.appId !== appId));
      if (currentApp?.appId === appId) {
        setCurrentApp(apps.find((app) => app.appId !== appId) || null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete app');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apps, currentApp]);

  // Check app readiness (schema + API key) — returns result for callers
  const checkAppReadiness = useCallback(async (appId) => {
    setAppReadiness(prev => ({ ...prev, [appId]: { ...prev[appId], loading: true } }));
    try {
      const [schemaRes, keysRes] = await Promise.allSettled([
        schemaApi.get(appId),
        apiKeysApi.list(appId),
      ]);
      const hasSchema = schemaRes.status === 'fulfilled' && !!schemaRes.value.data?.data;
      const hasApiKey = keysRes.status === 'fulfilled' && Array.isArray(keysRes.value.data?.data) && keysRes.value.data.data.length > 0;
      const result = { hasSchema, hasApiKey, loading: false };
      setAppReadiness(prev => ({
        ...prev,
        [appId]: result,
      }));
      return result;
    } catch {
      const fallback = { hasSchema: false, hasApiKey: false, loading: false };
      setAppReadiness(prev => ({ ...prev, [appId]: fallback }));
      return fallback;
    }
  }, []);
  checkReadinessRef.current = checkAppReadiness;

  // Select current app — validates ownership (admins bypass)
  const selectApp = useCallback((app) => {
    const ownsApp = apps.some(a => a.appId === app.appId);
    if (!ownsApp && !isAdmin) return;

    setCurrentApp(app);
    localStorage.setItem('currentAppId', app.appId);
    checkAppReadiness(app.appId);
  }, [apps, isAdmin, checkAppReadiness]);

  const value = {
    apps,
    currentApp,
    loading,
    error,
    appReadiness,
    fetchApps,
    fetchApp,
    createApp,
    updateApp,
    deleteApp,
    selectApp,
    setCurrentApp,
    checkAppReadiness,
  };

  return <AppsContext.Provider value={value}>{children}</AppsContext.Provider>;
}

export function useApps() {
  const context = useContext(AppsContext);
  if (!context) {
    throw new Error('useApps must be used within an AppsProvider');
  }
  return context;
}

export default AppsContext;
